import { api } from "shared/redux/api"
import {
  listOrders,
  getOrder,
  upsertOrder,
  setOrderStatus,
  listMyOrders,
  getMyOrderTracking,
  getRequesterCategoryBudget,
  listSuppliers,
  getSupplier,
  setSupplierKyc,
  getManagerBudget,
  getReport,
} from "./procurementMockData"
import { camelizeKeys } from "shared/utils/camelize"
import { buildListParams } from "./procurement"

// Procurement V2 service layer. Every endpoint still resolves against the
// in-memory mock store via `queryFn` EXCEPT Approval Rules, which now hits
// the real backend under `/private/procurement/v1/*` (ROLE_ADMIN gated — the
// axios base query attaches the bearer token because the URL contains
// "private"). Each remaining mock endpoint is a 1:1 placeholder for a real
// REST endpoint.
//
// TODO: replace every remaining `queryFn` below with a `query` hitting the
// real `/private/procurement/*` endpoint once the backend exists. Keep the
// hook names and argument shapes stable so callers don't change.
const ok = (data) => ({ data })

const APPROVAL_RULES_BASE = "/private/procurement/v1"
const BUDGET_BASE = "/private/procurement/v1"
const unwrap = (response) => camelizeKeys(response?.data)
// Guards list endpoints against a non-array body (paginated `{content:[...]}`,
// an error page routed through with a 200, etc.) so `.map` never throws.
const unwrapList = (response) => {
  const data = unwrap(response)
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.content)) return data.content
  if (Array.isArray(data?.items)) return data.items
  return []
}

const budgetDashboardQuery = (path) => ({
  query: ({ fiscalYear, fiscalMonth, centerId, categoryId, itemGroupId, itemId, documentType, keyword, page, size } = {}) => ({
    url: `${BUDGET_BASE}/budgets/dashboard/${path}`,
    params: buildListParams({
      fiscalYear,
      fiscalMonth,
      budgetCenterId: centerId,
      categoryId,
      itemGroupId,
      itemId,
      documentType,
      keyword,
      page,
      size,
    }),
  }),
  transformResponse: (response) => {
    const data = unwrap(response) || {}
    return { items: Array.isArray(data.items) ? data.items : [], total: data.total ?? 0 }
  },
  providesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
})

// GET /budgets now also returns center-level rollup numbers alongside `items`
// ({ centerId, centerName, totalBudget, committed, remaining, ... }) — the same
// figures the summary "number blocks" show, so the UI can render them straight
// from the response instead of re-summing `items` client-side.
// `centerId`/`centerName` === null just means the Center filter was left blank
// (budgetCenterId query-all across every center), NOT "no data".
// Returns null when the body is the legacy flat array (no top-level rollup).
const normalizeBudgetSummary = (response) => {
  const data = unwrap(response)
  if (!data || Array.isArray(data)) return null
  return {
    centerId: data.centerId ?? data.budgetCenterId ?? null,
    centerName: data.centerName ?? data.budgetCenterName ?? null,
    total: data.totalBudget ?? data.totalAmount ?? null,
    committed: data.committed ?? data.committedAmount ?? null,
    approvedUnpaid: data.approvedUnpaidAmount ?? data.approvedUnpaid ?? null,
    approvedPaid: data.approvedPaidAmount ?? data.approvedPaid ?? null,
    remaining: data.remaining ?? null,
    overPaidAmount: data.overPaidAmount ?? null,
    isOverBudget: !!data.isOverBudget,
    overSpentAmount: data.overSpentAmount ?? 0,
  }
}

// GET /budgets can answer "no budget for these filters" two ways: a real HTTP
// 404, or a soft one — HTTP 200 with `{ status: false, data: { code: "404",
// message } }`. Normalise the success-path (soft) case here into an empty
// result that still carries the API's message, so the screen collapses to that
// message instead of leaving a stale table from the previous fetch on screen.
const toBudgetResult = (response) => {
  const payload = unwrap(response)
  const softNotFound =
    !Array.isArray(payload) &&
    (String(payload?.code) === "404" || (payload?.status === false && !payload?.items))
  return {
    rows: softNotFound ? [] : unwrapList(response).map(normalizeBudgetRow),
    summary: softNotFound ? null : normalizeBudgetSummary(response),
    notFound: softNotFound,
    message: softNotFound ? payload?.message || payload?.data?.message || null : null,
  }
}

// Error path — flatten to `{ status, notFound, message }` so callers can treat
// "no budget" as an empty result (with the backend's message) rather than a
// hard error, and so the global error toast can skip it. "No budget" is
// recognised from the HTTP status OR the `code: "404"` the backend puts in the
// body regardless of the transport status it happens to use.
const toBudgetError = (error) => {
  const body = error?.data
  const code = body?.data?.code ?? body?.code
  const message =
    body?.data?.message || body?.message || (typeof body === "string" ? body : null)
  return {
    status: error?.status ?? null,
    notFound: error?.status === 404 || String(code) === "404",
    message,
    data: body,
  }
}

// GET /budgets `items[]` — one row per (category, center) combination:
// { categoryId, categoryName, centerId, centerName, totalAmount,
// committedAmount, approvedUnpaidAmount, approvedPaidAmount, remaining }.
// No `id`/fiscalMonth echoed back. Omitting fiscalMonth sums the 12 monthly
// rows for that year; omitting budgetCenterId returns every center's row
// separately (no longer silently summed together) — so this single response
// can drive BOTH the category rollup and the region/center breakdown,
// grouped client-side. Not safe to PUT against (no `id`) — use
// `getBudgetLineRows` (GET /budgets/list) for the editable per-row grid.
const normalizeBudgetRow = (row) => ({
  category: row.categoryId,
  categoryName: row.categoryName,
  centerId: row.centerId ?? row.budgetCenterId,
  centerName: row.centerName ?? row.budgetCenterName,
  total: row.totalAmount,
  committed: row.committedAmount,
  approvedUnpaid: row.approvedUnpaidAmount,
  approvedPaid: row.approvedPaidAmount,
  // `remaining` is BE-authoritative, not a client sum — with
  // accumulateUnspent it can be a multi-month cumulative figure, not just
  // this one period's total minus its own commitments.
  remaining: row.remaining,
  overPaidAmount: row.overPaidAmount ?? null,
  // BE-authoritative over-spend flag for THIS row's own remaining.
  isOverBudget: !!row.isOverBudget,
  overSpentAmount: row.overSpentAmount ?? 0,
  accumulateUnspent: row.accumulateUnspent,
  accumulatedThroughMonth: row.accumulatedThroughMonth,
})

// GET /budgets/list (raw, paged) — confirmed response nests `category:
// {id, name}` and `center: {id, sunCenterCode, name, isActive}`, one row per
// real pro.budget record (has `id`, safe for the PUT update).
const normalizeBudgetLine = (row) => ({
  id: row.id,
  categoryId: row.category?.id ?? row.categoryId ?? row.budgetCategoryId,
  categoryName: row.category?.name ?? row.categoryName,
  centerId: row.center?.id ?? row.centerId ?? row.budgetCenterId,
  centerCode: row.center?.sunCenterCode ?? row.sunCenterCode,
  centerName: row.center?.name ?? row.centerName,
  fiscalYear: row.fiscalYear,
  fiscalMonth: row.fiscalMonth ?? null,
  total: Number(row.totalAmount) || 0,
  committed: Number(row.committedAmount) || 0,
  approvedUnpaid: Number(row.approvedUnpaidAmount) || 0,
  approvedPaid: Number(row.approvedPaidAmount) || 0,
  // BE-authoritative — with accumulateUnspent this can be a multi-month
  // cumulative figure, so never recompute it as total - committed - ...
  // (that formula only ever held for a single, non-accumulating month).
  remaining: row.remaining,
  overPaidAmount: row.overPaidAmount ?? null,
  isOverBudget: !!row.isOverBudget,
  overSpentAmount: row.overSpentAmount ?? 0,
  accumulateUnspent: row.accumulateUnspent,
  accumulatedThroughMonth: row.accumulatedThroughMonth,
  note: row.note || "",
})

// GET /budgets/items — one row per (category, item group, item, center,
// month). `itemId === null` is an off-catalog spend line. `category.*` is the
// whole category × center figure for the month, REPEATED on every item of that
// group — display it per row, never sum it across rows.
const normalizeBudgetItem = (row) => ({
  categoryId: row.categoryId,
  categoryName: row.categoryName,
  itemGroupId: row.itemGroupId ?? null,
  itemGroupCode: row.itemGroupCode ?? null,
  itemGroupName: row.itemGroupName ?? null,
  itemId: row.itemId ?? null,
  itemCode: row.itemCode ?? null,
  itemName: row.itemName ?? null,
  centerId: row.budgetCenterId,
  centerName: row.budgetCenterName,
  fiscalYear: row.fiscalYear,
  fiscalMonth: row.fiscalMonth,
  requested: Number(row.requestedAmount) || 0,
  committed: Number(row.committedAmount) || 0,
  approvedUnpaid: Number(row.approvedUnpaidAmount) || 0,
  approvedPaid: Number(row.approvedPaidAmount) || 0,
  overPaid: Number(row.overPaidAmount) || 0,
  spentAndCommitted: Number(row.spentAndCommitted) || 0,
  categoryTotal: row.category?.totalAmount ?? null,
  categoryRemaining: row.category?.remaining ?? null,
  isOverBudget: !!row.category?.isOverBudget,
  overSpentAmount: row.category?.overSpentAmount ?? 0,
})

const procurementV2Api = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Order Requests ──────────────────────────────────────────────────────
    getOrderRequests: build.query({
      // TODO: replace with real endpoint GET /private/procurement/orders
      queryFn: (params = {}) => ok(listOrders(params)),
      providesTags: [{ type: "ORDER", id: "LIST" }],
    }),
    getOrderRequest: build.query({
      // TODO: replace with real endpoint GET /private/procurement/orders/:id
      queryFn: (id) => {
        const order = getOrder(id)
        return order ? ok(order) : { error: { status: 404, data: "Not found" } }
      },
      providesTags: (_r, _e, id) => [{ type: "ORDER", id }],
    }),
    saveOrderRequest: build.mutation({
      // TODO: replace with real endpoint POST/PUT /private/procurement/orders
      queryFn: (body) => ok(upsertOrder(body)),
      invalidatesTags: (r) => [
        { type: "ORDER", id: "LIST" },
        { type: "ORDER", id: r?.id },
        { type: "MYORDER", id: "LIST" },
      ],
    }),
    setOrderRequestStatus: build.mutation({
      // TODO: replace with real endpoint POST /private/procurement/orders/:id/status
      queryFn: ({ id, status }) => ok(setOrderStatus(id, status)),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER", id: "LIST" },
        { type: "ORDER", id },
        { type: "MYORDER", id: "LIST" },
        { type: "MYORDER", id },
      ],
    }),

    // ── My Orders tracking ──────────────────────────────────────────────────
    getMyOrders: build.query({
      // TODO: replace with real endpoint GET /private/procurement/my-orders
      queryFn: (params = {}) => ok(listMyOrders(params)),
      providesTags: [{ type: "MYORDER", id: "LIST" }],
    }),
    getMyOrderTracking: build.query({
      // TODO: replace with real endpoint GET /private/procurement/my-orders/:id
      queryFn: (id) => {
        const t = getMyOrderTracking(id)
        return t ? ok(t) : { error: { status: 404, data: "Not found" } }
      },
      providesTags: (_r, _e, id) => [{ type: "MYORDER", id }],
    }),

    // ── Requester budget (read-only, by fiscal year) ────────────────────────
    // GET /private/procurement/v1/budget-categories?is_active=
    // Named distinctly from legacy `procurement.js`'s `getBudgetCategories` —
    // that endpoint is injected into the same RTK `api` slice with no
    // transformResponse (returns the raw `{status,message,data}` envelope),
    // and a same-name injection here would silently lose to whichever module
    // loads first instead of raising a conflict.
    // `mustHaveOrder`/`mustHavePurchase` (Boolean, optional) filter to
    // categories flagged for the "create PO/Payment without Order/PO" flow
    // (2026-09 barrier-removal feature) — bỏ trống = không lọc, giữ hành vi cũ.
    getActiveBudgetCategories: build.query({
      query: (params = {}) => ({
        url: `${BUDGET_BASE}/budget-categories`,
        params: {
          is_active: params.isActive,
          must_have_order: params.mustHaveOrder,
          must_have_purchase: params.mustHavePurchase,
        },
      }),
      transformResponse: unwrapList,
      providesTags: [{ type: "REQ-BUDGET", id: "CATEGORIES" }],
    }),
    // POST /private/procurement/v1/budget-categories — name required.
    // `code` was previously silently dropped here (never sent) — fixed
    // alongside adding mustHaveOrder/mustHavePurchase since the new Category
    // management UI needs it. Bỏ trống mustHaveOrder/mustHavePurchase lúc
    // create → BE tự default `true`.
    createBudgetCategory: build.mutation({
      query: (body) => ({
        url: `${BUDGET_BASE}/budget-categories`,
        method: "POST",
        body: {
          name: body.name,
          code: body.code || undefined,
          assetClassCode: body.assetClassCode || undefined,
          sunPlAccount: body.sunPlAccount || undefined,
          sunBsAccount: body.sunBsAccount || undefined,
          teamId: body.teamId || undefined,
          isActive: body.isActive ?? true,
          mustHaveOrder: body.mustHaveOrder,
          mustHavePurchase: body.mustHavePurchase,
        },
      }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "REQ-BUDGET", id: "CATEGORIES" }],
    }),
    // PUT /private/procurement/v1/budget-categories/:id — same shape as
    // create, name required, id unchanged. Bỏ trống mustHaveOrder/
    // mustHavePurchase → BE giữ nguyên giá trị cũ.
    updateBudgetCategory: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BUDGET_BASE}/budget-categories/${id}`,
        method: "PUT",
        body: {
          name: body.name,
          code: body.code || undefined,
          assetClassCode: body.assetClassCode || undefined,
          sunPlAccount: body.sunPlAccount || undefined,
          sunBsAccount: body.sunBsAccount || undefined,
          teamId: body.teamId || undefined,
          isActive: body.isActive ?? true,
          mustHaveOrder: body.mustHaveOrder,
          mustHavePurchase: body.mustHavePurchase,
        },
      }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "REQ-BUDGET", id: "CATEGORIES" }],
    }),
    // GET /private/procurement/v1/budgets?fiscalYear= — 404 means no budget
    // allocated for that year yet; the requester screen already renders an
    // empty state whenever `data` is undefined, so no special-casing needed.
    getRequesterBudget: build.query({
      // Callers pass `centerId` (the FE's naming); the wire param is
      // `budgetCenterId`. `categoryId` (optional) scopes the summary to one
      // budgetCategoryId — omit to get every category's row, same as before.
      query: ({ fiscalYear, fiscalMonth, centerId, categoryId } = {}) => ({
        url: `${BUDGET_BASE}/budgets`,
        params: {
          fiscalYear,
          fiscalMonth: fiscalMonth || undefined,
          budgetCenterId: centerId || undefined,
          categoryId: categoryId || undefined,
        },
      }),
      // Response: { ...centerRollup, items: [ ...per-category rows ] }. Expose
      // `rows` for the table, `summary` for the number blocks, and
      // `notFound`/`message` so a soft 404 collapses the screen to the message.
      transformResponse: toBudgetResult,
      transformErrorResponse: toBudgetError,
      providesTags: [{ type: "REQ-BUDGET", id: "LIST" }],
    }),
    getRequesterCategoryBudget: build.query({
      // TODO: replace with real endpoint GET /private/procurement/budgets/:categoryId?fiscalYear=
      queryFn: ({ categoryId, fiscalYear } = {}) =>
        ok(getRequesterCategoryBudget(categoryId, fiscalYear)),
      providesTags: (_r, _e, arg) => [
        { type: "REQ-BUDGET", id: arg?.categoryId },
      ],
    }),

    // ── Manager budget lines (editable, pro.budget grain) ───────────────────
    // GET /private/procurement/v1/budgets?fiscalYear=&fiscalMonth=&budgetCenterId=
    // Summary endpoint, grouped by category+center: when fiscalMonth is
    // omitted the backend sums the 12 monthly rows for that year; when
    // budgetCenterId is omitted (Center filter left blank) it queries across
    // every center and each still gets its own `items[]` row. Rows here have NO `id` — good for
    // the read-only rollup tables (grouped further client-side by category
    // or by center), NOT safe to PUT against. Use `getBudgetLineRows` (GET
    // /budgets/list) instead for an editable per-row grid.
    getBudgetLines: build.query({
      query: ({ fiscalYear, fiscalMonth, centerId } = {}) => ({
        url: `${BUDGET_BASE}/budgets`,
        params: {
          fiscalYear,
          fiscalMonth: fiscalMonth || undefined,
          budgetCenterId: centerId || undefined,
        },
      }),
      transformResponse: toBudgetResult,
      transformErrorResponse: toBudgetError,
      providesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // GET /private/procurement/v1/budgets/items?fiscalYear=&fiscalMonth=&budgetCenterId=&categoryId=&itemGroupId=&itemId=
    // Item-level drill-down of the same budgets. Only `fiscalYear` is required;
    // empty data comes back as `[]` (never 404).
    getBudgetItems: build.query({
      query: ({ fiscalYear, fiscalMonth, centerId, categoryId, itemGroupId, itemId } = {}) => ({
        url: `${BUDGET_BASE}/budgets/items`,
        params: {
          fiscalYear,
          fiscalMonth: fiscalMonth || undefined,
          budgetCenterId: centerId || undefined,
          categoryId: categoryId || undefined,
          itemGroupId: itemGroupId || undefined,
          itemId: itemId || undefined,
        },
      }),
      transformResponse: (response) => unwrapList(response).map(normalizeBudgetItem),
      providesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // GET /private/procurement/v1/budgets/allocation-matrix?fiscalYear=&fiscalMonth=
    // Returns every PO (one row per PO x OR line); year/month only pick which
    // month's budget bucket is joined in. Callers filter to their own POs.
    getBudgetAllocationMatrix: build.query({
      query: ({ fiscalYear, fiscalMonth } = {}) => ({
        url: `${BUDGET_BASE}/budgets/allocation-matrix`,
        params: { fiscalYear, fiscalMonth },
      }),
      transformResponse: unwrapList,
      providesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // GET /private/procurement/v1/budgets/non-po-preview?centerId=&categoryId=&fiscalYear=&fiscalMonth=&thisPayment=
    // One call per center (BE commits a Non-PO per center on submit), so this
    // takes `centers: [{ centerId, thisPayment }]` and returns the previews in order.
    getNonPoBudgetPreview: build.query({
      async queryFn({ categoryId, fiscalYear, fiscalMonth, centers = [] }, _api, _extra, baseQuery) {
        const results = await Promise.all(
          centers.map((c) =>
            baseQuery({
              url: `${BUDGET_BASE}/budgets/non-po-preview`,
              params: { centerId: c.centerId, categoryId, fiscalYear, fiscalMonth, thisPayment: c.thisPayment },
            })
          )
        )
        const failed = results.find((r) => r.error)
        if (failed) return { error: failed.error }
        return { data: results.map((r) => unwrap(r.data)) }
      },
      providesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // GET /private/procurement/v1/budgets/dashboard/* (B1–B7, Finance Reports).
    // All paged as { items, total, page, size }; fiscalYear + fiscalMonth required,
    // allocation-by-center and detail also need a center or category (400 otherwise).
  // detail also takes documentType (OR | PO | PR) and keyword.
    getBudgetDashboardOverview: build.query(budgetDashboardQuery("overview")),
    getBudgetDashboardByCategory: build.query(budgetDashboardQuery("by-category")),
    getBudgetDashboardAllocationByCenter: build.query(budgetDashboardQuery("allocation-by-center")),
    getBudgetDashboardSpendingByCenter: build.query(budgetDashboardQuery("spending-by-center")),
    getBudgetDashboardHeatmap: build.query(budgetDashboardQuery("heatmap")),
    getBudgetDashboardTrend: build.query(budgetDashboardQuery("trend")),
    getBudgetDashboardDetail: build.query(budgetDashboardQuery("detail")),
    // Orders with their OR lines nested (`lines[]`), paged by Order — the "Order" tab of B7.
    getBudgetDashboardDetailOrders: build.query(budgetDashboardQuery("detail/orders")),
    // GET /private/procurement/v1/budgets/list?categoryId=&budgetCenterId=&fiscalYear=&fiscalMonth=&page=&size=
    // Raw, un-aggregated per-row budget records (paged) — one pro.budget row
    // per (category, center, year, month). This is the endpoint the Manager
    // editable table actually reads from, since every row here carries a real
    // DB `id` safe to PUT against (unlike the summary `getBudgetLines`
    // above). No pagination UI yet — fetches a single large page.
    getBudgetLineRows: build.query({
      query: ({ categoryId, centerId, fiscalYear, fiscalMonth, page = 0, size = 200 } = {}) => ({
        url: `${BUDGET_BASE}/budgets/list`,
        params: {
          categoryId: categoryId || undefined,
          budgetCenterId: centerId || undefined,
          fiscalYear,
          fiscalMonth: fiscalMonth || undefined,
          page,
          size,
        },
      }),
      transformResponse: (response) => unwrapList(response).map(normalizeBudgetLine),
      transformErrorResponse: toBudgetError,
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: "BUDGET-LINE", id: r.id })),
              { type: "BUDGET-LINE", id: "LIST" },
            ]
          : [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // POST /private/procurement/v1/budgets — fiscalMonth is mandatory (1-12).
    // If the (category, center, year, month) row already exists, BE adds
    // totalAmount onto it instead of rejecting; a sent `note` overwrites the
    // old one. Requires ROLE_FINANCE_LEAD or ROLE_ADMIN.
    createBudgetLine: build.mutation({
      // `accumulateUnspent` is a standing policy for the whole (category,
      // center) pair — BE upserts it whenever it's present, so callers omit
      // it when adding onto an existing month to leave the policy alone.
      query: (body) => ({
        url: `${BUDGET_BASE}/budgets`,
        method: "POST",
        body: {
          budgetCategoryId: body.categoryId,
          budgetCenterId: body.centerId,
          fiscalYear: body.fiscalYear,
          fiscalMonth: body.fiscalMonth,
          totalAmount: body.total,
          note: body.note || undefined,
          accumulateUnspent: body.accumulateUnspent,
        },
      }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // PUT /private/procurement/v1/budgets/:id — only totalAmount/note are
    // editable; category/center/year/month are immutable (changing any of
    // those 4 dimensions means creating a new row instead). Committed /
    // approved amounts stay server-owned (ledger-derived) and remaining is
    // computed client-side, never sent. Same role gate as create.
    updateBudgetLine: build.mutation({
      query: ({ id, total, note }) => ({
        url: `${BUDGET_BASE}/budgets/${id}`,
        method: "PUT",
        // PUT overwrites note — always send the current one; empty maps to null.
        body: { totalAmount: total, note: note || null },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "BUDGET-LINE", id },
        { type: "BUDGET-LINE", id: "LIST" },
      ],
    }),
    // PATCH /private/procurement/v1/budgets/:id/allocation-policy — flag-only
    // change (PUT would require totalAmount and null out note). The flag
    // applies to the whole (category, center) pair, so every month's
    // `remaining` can shift — invalidate the whole list, not just this row.
    updateBudgetAllocationPolicy: build.mutation({
      query: ({ id, accumulateUnspent }) => ({
        url: `${BUDGET_BASE}/budgets/${id}/allocation-policy`,
        method: "PATCH",
        body: { accumulateUnspent },
      }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "BUDGET-LINE", id: "LIST" }],
    }),
    // GET /private/procurement/v1/budget-centers?is_active= — list of
    // pro.budget_center rows (seeded from apollo2.sun_center), used to build
    // the Center dropdown. Confirmed response: { id, sunCenterCode, name, isActive }.
    getBudgetCenters: build.query({
      query: ({ isActive } = {}) => ({
        url: `${BUDGET_BASE}/budget-centers`,
        params: { is_active: isActive },
      }),
      transformResponse: (response) =>
        unwrapList(response).map((c) => ({
          id: c.id,
          centerCode: c.sunCenterCode,
          name: c.name,
          isActive: c.isActive,
        })),
      providesTags: [{ type: "BUDGET-CENTER", id: "LIST" }],
    }),

    // ── Suppliers ───────────────────────────────────────────────────────────
    getSuppliersV2: build.query({
      // TODO: replace with real endpoint GET /private/procurement/suppliers
      queryFn: (params = {}) => ok(listSuppliers(params)),
      providesTags: [{ type: "SUPPLIER", id: "LIST" }],
    }),
    getSupplierDetail: build.query({
      // TODO: replace with real endpoint GET /private/procurement/suppliers/:id
      queryFn: (id) => {
        const s = getSupplier(id)
        return s ? ok(s) : { error: { status: 404, data: "Not found" } }
      },
      providesTags: (_r, _e, id) => [{ type: "SUPPLIER", id }],
    }),
    setSupplierKycStatus: build.mutation({
      // TODO: replace with real endpoint POST /private/procurement/suppliers/:id/kyc
      queryFn: ({ id, kycStatus }) => ok(setSupplierKyc(id, kycStatus)),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SUPPLIER", id: "LIST" },
        { type: "SUPPLIER", id },
      ],
    }),

    // ── Manager budget / report ─────────────────────────────────────────────
    getManagerBudget: build.query({
      // TODO: replace with real endpoint GET /private/procurement/budget/overview
      queryFn: (params = {}) => ok(getManagerBudget(params)),
      providesTags: [{ type: "MGR-BUDGET", id: "OVERVIEW" }],
    }),
    getProcurementReport: build.query({
      // TODO: replace with real endpoint GET /private/procurement/report
      queryFn: (params = {}) => ok(getReport(params)),
      providesTags: [{ type: "PROC-REPORT", id: "MAIN" }],
    }),

    // ── Approval rules (admin) + shared preview ─────────────────────────────
    // GET /private/procurement/v1/approval-rules?entity=&category=&region=&page=&size=
    getApprovalRules: build.query({
      query: (params = {}) => ({
        url: `${APPROVAL_RULES_BASE}/approval-rules`,
        params: {
          entity: params.entity || undefined,
          category: params.category || undefined,
          region: params.region || undefined,
          page: params.page,
          size: params.size,
        },
      }),
      transformResponse: unwrap,
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: "APPROVAL-RULE", id: r.id })),
              { type: "APPROVAL-RULE", id: "LIST" },
            ]
          : [{ type: "APPROVAL-RULE", id: "LIST" }],
    }),
    // GET /private/procurement/v1/approval-rules/{id}
    getApprovalRule: build.query({
      query: (id) => ({ url: `${APPROVAL_RULES_BASE}/approval-rules/${id}` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "APPROVAL-RULE", id }],
    }),
    // POST (create, no id) or PUT (update, has id) /approval-rules[/:id]
    saveApprovalRule: build.mutation({
      query: (draft) => {
        const ruleData = draft.rule || draft
        if (ruleData.id) {
          return {
            url: `${APPROVAL_RULES_BASE}/approval-rules/${ruleData.id}`,
            method: "PUT",
            body: {
              name: ruleData.name,
              conditions: draft.conditions || [],
              steps: draft.steps || [],
              // Always sent (even []) — the editor owns the full notifier
              // list, so there's no "leave unchanged" case here.
              notifierEmails: (ruleData.notifierEmails || "")
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
            },
          }
        }
        // Create only accepts { name, entityType } — the backend rejects
        // conditions/steps here. The container's handleSaveDraft follows this
        // up with an immediate PUT (using the returned id) so conditions/steps
        // from a brand-new rule's first save still land — see there.
        return {
          url: `${APPROVAL_RULES_BASE}/approval-rules`,
          method: "POST",
          body: {
            name: ruleData.name,
            entityType: ruleData.entityType,
          },
        }
      },
      transformResponse: unwrap,
      invalidatesTags: (result) => [
        { type: "APPROVAL-RULE", id: "LIST" },
        { type: "APPROVAL-RULE", id: result?.rule?.id },
      ],
    }),
    // POST /approval-rules/{id}/publish
    publishApprovalRule: build.mutation({
      query: (id) => ({
        url: `${APPROVAL_RULES_BASE}/approval-rules/${id}/publish`,
        method: "POST",
      }),
      transformResponse: unwrap,
      invalidatesTags: (result, _e, id) => [
        { type: "APPROVAL-RULE", id: "LIST" },
        { type: "APPROVAL-RULE", id: result?.id ?? id },
      ],
    }),
    // PATCH /approval-rules/{id} — toggle isActive
    toggleApprovalRuleActive: build.mutation({
      query: ({ id, isActive }) => ({
        url: `${APPROVAL_RULES_BASE}/approval-rules/${id}`,
        method: "PATCH",
        body: { isActive },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "APPROVAL-RULE", id: "LIST" },
        { type: "APPROVAL-RULE", id },
      ],
    }),
    // Shared approver-chain preview consumed by Admin config AND the requester's
    // Approval Route Preview AND manager approval routing.
    // GET /private/procurement/v1/approval/preview?entity=&category=&amount=&region=&center=&urgency=
    // Was previously mounted (duplicated) under /approval-rules/approval/preview
    // on ApprovalRuleController — that duplicate has been deleted server-side;
    // this now points at the single source of truth on OrderRequestController
    // (same path procurementApprovals.js's `previewOrderApproval` already uses).
    previewApprovalChain: build.query({
      query: (ctx = {}) => ({
        url: `${APPROVAL_RULES_BASE}/approval/preview`,
        params: {
          entity: ctx.entity || undefined,
          category: ctx.category || undefined,
          amount: ctx.amount || undefined,
          region: ctx.region || undefined,
          center: ctx.center || undefined,
          urgency: ctx.urgency || undefined,
        },
      }),
      transformResponse: unwrap,
    }),

    // Manager PR approval decision (approve / reject / info_request). PRs are
    // real entities, so this is a thin placeholder over the real workflow.
    setPrApprovalDecision: build.mutation({
      // TODO: replace with real endpoint POST /private/procurement/requests/:id/decision
      queryFn: ({ id, decision }) => ok({ id, decision }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PR", id },
        { type: "PR", id: "LIST" },
        { type: "PR-APPROVAL", id },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetOrderRequestsQuery,
  useGetOrderRequestQuery,
  useSaveOrderRequestMutation,
  useSetOrderRequestStatusMutation,
  useGetMyOrdersQuery,
  useGetMyOrderTrackingQuery,
  useGetActiveBudgetCategoriesQuery,
  useCreateBudgetCategoryMutation,
  useUpdateBudgetCategoryMutation,
  useGetRequesterBudgetQuery,
  useGetRequesterCategoryBudgetQuery,
  useGetBudgetLinesQuery,
  useGetBudgetLineRowsQuery,
  useLazyGetBudgetLineRowsQuery,
  useGetBudgetItemsQuery,
  useGetBudgetAllocationMatrixQuery,
  useLazyGetBudgetAllocationMatrixQuery,
  useGetNonPoBudgetPreviewQuery,
  useGetBudgetDashboardOverviewQuery,
  useGetBudgetDashboardByCategoryQuery,
  useGetBudgetDashboardAllocationByCenterQuery,
  useGetBudgetDashboardSpendingByCenterQuery,
  useGetBudgetDashboardHeatmapQuery,
  useGetBudgetDashboardTrendQuery,
  useGetBudgetDashboardDetailQuery,
  useGetBudgetDashboardDetailOrdersQuery,
  useCreateBudgetLineMutation,
  useUpdateBudgetLineMutation,
  useUpdateBudgetAllocationPolicyMutation,
  useGetBudgetCentersQuery,
  useGetSuppliersV2Query,
  useGetSupplierDetailQuery,
  useSetSupplierKycStatusMutation,
  useGetManagerBudgetQuery,
  useGetProcurementReportQuery,
  useGetApprovalRulesQuery,
  useGetApprovalRuleQuery,
  useSaveApprovalRuleMutation,
  usePublishApprovalRuleMutation,
  useToggleApprovalRuleActiveMutation,
  usePreviewApprovalChainQuery,
  useSetPrApprovalDecisionMutation,
} = procurementV2Api

export default procurementV2Api
